<template>
  <div
    ref="container"
    class="post-hashtags"
  >
    <div class="tags-row">
      <span
        v-for="tag in content.hashtags"
        :key="tag.id"
        class="hashtag-badge"
        :style="badgeStyle(tag.color)"
      >
        #{{ tag.label }}
        <button
          class="remove-tag-btn"
          :style="{ color: tag.color }"
          @click="removeTag(tag)"
        >
          <i class="fa fa-times" />
        </button>
      </span>

      <button
        class="add-tag-btn"
        @click="openPicker"
      >
        <i class="fa fa-tag mr-1" />
        Etiquetar
      </button>
    </div>

    <portal to="news-feed-portal">
      <div
        v-if="showPicker"
        class="tag-picker"
        :style="pickerStyle"
      >
        <div class="picker-search-wrapper">
          <i class="fa fa-search picker-search-icon" />
          <input
            ref="searchInput"
            v-model="searchText"
            class="picker-search"
            placeholder="Buscar etiqueta..."
            @input="onSearchInput"
          >
        </div>

        <div class="picker-list">
          <div
            v-if="loadingTags"
            class="picker-spinner"
          >
            <b-spinner
              small
              variant="secondary"
            />
          </div>

          <template v-else>
            <button
              v-for="tag in filteredAvailable"
              :key="tag.id"
              class="picker-item"
              @click="addTag(tag)"
            >
              <span
                class="picker-dot"
                :style="{ background: tag.color }"
              />
              <span class="picker-label">{{ tag.label }}</span>
            </button>

            <p
              v-if="filteredAvailable.length === 0"
              class="picker-empty"
            >
              Sin resultados
            </p>
          </template>
        </div>
      </div>
    </portal>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from '@vue/composition-api'
import { getHashtags, addPostHashtags, removePostHashtag } from '../Services'

export default {
  name: 'PostHashtags',
  props: {
    content: {
      type: Object,
      required: true
    }
  },
  setup (props, context) {
    const showPicker = ref(false)
    const searchText = ref('')
    const allTags = ref([])
    const loadingTags = ref(false)
    const container = ref(null)
    const searchInput = ref(null)
    const pickerStyle = ref({})

    const badgeStyle = (color) => ({
      backgroundColor: color + '18',
      color,
      border: `1px solid ${color}40`
    })

    const filteredAvailable = computed(() => {
      const current = new Set((props.content.hashtags || []).map(t => t.id))
      return allTags.value.filter(t =>
        !current.has(t.id) &&
        (searchText.value === '' || t.label.toLowerCase().includes(searchText.value.toLowerCase()))
      )
    })

    const calcPickerPosition = () => {
      if (!container.value) return
      const rect = container.value.getBoundingClientRect()
      const spaceBelow = window.innerHeight - rect.bottom
      const pickerHeight = 260

      if (spaceBelow >= pickerHeight) {
        pickerStyle.value = {
          position: 'fixed',
          top: `${rect.bottom + 4}px`,
          left: `${rect.left}px`,
          zIndex: 9999
        }
      } else {
        pickerStyle.value = {
          position: 'fixed',
          bottom: `${window.innerHeight - rect.top + 4}px`,
          left: `${rect.left}px`,
          zIndex: 9999
        }
      }
    }

    const fetchTags = async () => {
      if (allTags.value.length > 0) return
      loadingTags.value = true
      try {
        allTags.value = await getHashtags()
      } catch {
        // silent
      } finally {
        loadingTags.value = false
      }
    }

    const openPicker = async () => {
      calcPickerPosition()
      showPicker.value = true
      searchText.value = ''
      await fetchTags()
      context.root.$nextTick(() => {
        if (searchInput.value) searchInput.value.focus()
      })
    }

    const closePicker = () => {
      showPicker.value = false
    }

    const onSearchInput = async () => {
      loadingTags.value = true
      try {
        allTags.value = await getHashtags(searchText.value)
      } catch {
        // silent
      } finally {
        loadingTags.value = false
      }
    }

    const addTag = async (tag) => {
      try {
        await addPostHashtags(props.content.id, [tag.id])
        if (!props.content.hashtags) props.content.hashtags = []
        props.content.hashtags.push(tag)
        context.emit('hashtag-added')
        closePicker()
      } catch {
        closePicker()
        context.root.$bvToast.toast('No se pudo agregar la etiqueta. Intenta nuevamente.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 3000
        })
      }
    }

    const removeTag = async (tag) => {
      try {
        await removePostHashtag(props.content.id, tag.id)
        const idx = props.content.hashtags.findIndex(t => t.id === tag.id)
        if (idx !== -1) props.content.hashtags.splice(idx, 1)
        context.emit('hashtag-added')
      } catch {
        context.root.$bvToast.toast('No se pudo quitar la etiqueta. Intenta nuevamente.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 3000
        })
      }
    }

    const onClickOutside = (e) => {
      const inContainer = container.value && container.value.contains(e.target)
      const inPicker = !!e.target.closest('.tag-picker')
      if (!inContainer && !inPicker) closePicker()
    }

    onMounted(() => document.addEventListener('click', onClickOutside))
    onBeforeUnmount(() => document.removeEventListener('click', onClickOutside))

    return {
      showPicker,
      searchText,
      allTags,
      loadingTags,
      container,
      searchInput,
      pickerStyle,
      badgeStyle,
      filteredAvailable,
      openPicker,
      onSearchInput,
      addTag,
      removeTag
    }
  }
}
</script>

<style scoped>
.post-hashtags {
  position: relative;
  margin-top: 0.6rem;
}

.tags-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
}

.hashtag-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.18rem 0.5rem 0.18rem 0.6rem;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: 600;
  line-height: 1;
  user-select: none;
}

.remove-tag-btn {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 0.6rem;
  line-height: 1;
  opacity: 0.6;
  display: flex;
  align-items: center;
  transition: opacity 0.15s;
}

.remove-tag-btn:hover { opacity: 1; }

.add-tag-btn {
  background: none;
  border: 1px dashed #cbd5e0;
  border-radius: 10px;
  padding: 0.18rem 0.55rem;
  font-size: 0.7rem;
  font-weight: 500;
  color: #a0aec0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  transition: border-color 0.15s, color 0.15s;
}

.add-tag-btn:hover {
  border-color: #667eea;
  color: #667eea;
}

/* Picker — posicionado via portal con fixed desde JS */
.tag-picker {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  width: 220px;
  overflow: hidden;
}

.picker-search-wrapper {
  position: relative;
  border-bottom: 1px solid #f0f4f8;
}

.picker-search-icon {
  position: absolute;
  left: 0.65rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.72rem;
  color: #a0aec0;
  pointer-events: none;
}

.picker-search {
  width: 100%;
  border: none;
  outline: none;
  padding: 0.55rem 0.75rem 0.55rem 2rem;
  font-size: 0.78rem;
  color: #2d3748;
  background: transparent;
}

.picker-list {
  max-height: 200px;
  overflow-y: auto;
  padding: 0.3rem 0;
}

.picker-spinner {
  display: flex;
  justify-content: center;
  padding: 0.75rem;
}

.picker-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.45rem 0.85rem;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  transition: background 0.1s;
}

.picker-item:hover { background: #f7fafc; }

.picker-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.picker-label {
  font-size: 0.78rem;
  color: #2d3748;
  font-weight: 500;
}

.picker-empty {
  font-size: 0.75rem;
  color: #a0aec0;
  text-align: center;
  padding: 0.75rem;
  margin: 0;
}
</style>



// WEBPACK FOOTER //
// src/app/news_feed/components/PostHashtags.vue